import React, { useState, useRef } from "react";
import GetRequest from "../Services/APIRequest";
import Loading from "../Components/Loading";
import Error from "../Components/Error";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";




// Helper: Indian number format with 2 decimal places
const formatIndianCurrency = (value) => {
  if (value === undefined || value === null) return "0.00";
  const num = typeof value === "number" ? value : Number(value);
  if (isNaN(num)) return "0.00";

  const rounded = Math.round(num * 100) / 100;
  return rounded.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const DistributorSummaryReport = () => {
  const reportRef = useRef();
  const distID = localStorage.getItem("distributorId");

  // Default current financial year
  const [selectedFY, setSelectedFY] = useState("26-27");

  const financialYears = ["24-25", "25-26", "26-27"];

  const { data, error, isLoaded } = GetRequest(
    `/Account/GetDistributorSummaryReport2/${distID}/${selectedFY}`
  );

  if (error) return <Error data="Distributor Summary" />;
  if (!isLoaded) return <Loading />;

  const {
    costCenterTransactionsAndBalances,
    costCenterWiseNetBusinessValue,
    productWiseSales,
  } = data.result;

  // ---------- 1. Net Business Value ----------
  const categoryOrder = ["Seeds", "Pesticides", "Fertilizers", "PGR"];

  const sortedNetBusiness = [...costCenterWiseNetBusinessValue].sort((a, b) => {
    const idxA = categoryOrder.indexOf(a.costCentreSalesCategoryName);
    const idxB = categoryOrder.indexOf(b.costCentreSalesCategoryName);

    if (idxA === -1 && idxB === -1) return 0;
    if (idxA === -1) return 1;
    if (idxB === -1) return -1;

    return idxA - idxB;
  });

  const getAmount = (cat) =>
    sortedNetBusiness.find((x) => x.costCentreSalesCategoryName === cat)
      ?.netAmount || 0;

  const seedsAmt = getAmount("Seeds");
  const pesticidesAmt = getAmount("Pesticides");
  const fertilizersAmt = getAmount("Fertilizers");
  const pgrAmt = getAmount("PGR");

  const totalPFP = pesticidesAmt + fertilizersAmt + pgrAmt;
  const grandTotal = seedsAmt + totalPFP;

  const netBusinessWithTotals = [
    ...sortedNetBusiness,
    { costCentreSalesCategoryName: "Total PFP", netAmount: totalPFP },
    { costCentreSalesCategoryName: "Grand Total", netAmount: grandTotal },
  ];

  // ---------- 2. Cost Centre Transactions ----------
  const sortedList = (costCenterTransactionsAndBalances || []).sort(
    (a, b) => a.sequenceNumber - b.sequenceNumber
  );

  const totals = sortedList.reduce(
    (acc, item) => {
      acc.opening += item.opening || 0;
      acc.debit += item.debitAmount || 0;
      acc.credit += item.creditAmount || 0;
      acc.closing += item.closing || 0;
      return acc;
    },
    { opening: 0, debit: 0, credit: 0, closing: 0 }
  );

  // ---------- 3. Product-wise Sales ----------
  const groupedProducts = productWiseSales.reduce((acc, curr) => {
    if (!acc[curr.productCategory]) acc[curr.productCategory] = [];
    acc[curr.productCategory].push(curr);
    return acc;
  }, {});

  const downloadPDF = async () => {
    const input = reportRef.current;

    const canvas = await html2canvas(input, {
      scale: 2,
      useCORS: true,
      scrollY: -window.scrollY,
    });

    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "mm", "a4");

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pdfHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;
    }

    pdf.save(`Distributor_Summary_Report_${selectedFY}.pdf`);
  };

  return (
    <section className="content">
      <div className="row mb-3">
        <div className="col-12 d-flex justify-content-between align-items-center flex-wrap px-2 px-md-4">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="fw-bold">FY:</span>
            {financialYears.map((fy) => (
              <button
                key={fy}
                className={`btn btn-sm ${selectedFY === fy ? "btn-primary" : "btn-outline-primary"
                  }`}
                onClick={() => setSelectedFY(fy)}
              >
                {fy}
              </button>
            ))}
          </div>

          <button className="btn btn-success btn-sm" onClick={downloadPDF}>
            Download
          </button>
        </div>
      </div>
      <div className="container-fluid px-2 px-md-4">
        <div ref={reportRef}>

          {/* Report Header */}
          <div className="row mb-4">
            <div className="col-12">
              <div
                className="card shadow-sm border-0"
                style={{
                  background: "linear-gradient(135deg,  rgba(133, 207, 115, 0.48), rgb(199 222 193))",
                  color: "white",
                  borderRadius: "12px",
                }}
              >
                <div className="card-body py-4 text-center">
                  <h3 className="fw-bold mb-2">
                    Summary Report
                  </h3>

                  <h4 className="fw-bold mb-2">
                    {data.result.distributorName || "N/A"}
                  </h4>

                  <div className="d-flex justify-content-center gap-4 flex-wrap">
                    <span className="badge bg-light text-dark px-3 py-2 fs-6">
                      Financial Year: {selectedFY}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 1. Net Business Value */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header bg-info text-white">
                  <h5 className="card-title mb-0">
                    1. Net Business Value (Cost Centre-wise)
                  </h5>
                </div>
                <div className="card-body table-responsive p-2">
                  <table className="table table-bordered table-hover table-sm mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="text-center">Sales Category</th>
                        <th className="text-center">Net Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {netBusinessWithTotals.map((item, idx) => (
                        <tr
                          key={idx}
                          className={
                            item.costCentreSalesCategoryName.includes("Total")
                              ? "table-secondary fw-bold"
                              : ""
                          }
                        >
                          <td>{item.costCentreSalesCategoryName}</td>
                          <td className="text-end">
                            {formatIndianCurrency(item.netAmount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* 2. Cost Centre Transactions & Balances */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header bg-warning text-white">
                  <h5 className="card-title mb-0">
                    2. Cost Centre Transactions & Balances
                  </h5>
                </div>
                <div className="card-body table-responsive p-2">
                  <table className="table table-bordered table-hover table-sm mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: "60px" }} className="text-center">
                          Seq
                        </th>
                        <th className="text-center">Cost Centre</th>
                        <th className="text-center">Op</th>
                        <th className="text-center">Dr</th>
                        <th className="text-center">Cr</th>
                        <th className="text-center">Closing</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedList.map((item, index) => (
                        <tr key={index}>
                          <td className="text-center">{item.sequenceNumber}</td>
                          <td>{item.costCentreName}</td>
                          <td className="text-end">
                            {formatIndianCurrency(item.opening)}
                          </td>
                          <td className="text-end">
                            {formatIndianCurrency(item.debitAmount)}
                          </td>
                          <td className="text-end">
                            {formatIndianCurrency(item.creditAmount)}
                          </td>
                          <td className="text-end fw-bold text-success">
                            {formatIndianCurrency(item.closing)}
                          </td>
                        </tr>
                      ))}

                      <tr className="table-secondary fw-bold">
                        <td colSpan="2">Total</td>
                        <td className="text-end">
                          {formatIndianCurrency(totals.opening)}
                        </td>
                        <td className="text-end">
                          {formatIndianCurrency(totals.debit)}
                        </td>
                        <td className="text-end">
                          {formatIndianCurrency(totals.credit)}
                        </td>
                        <td className="text-end">
                          {formatIndianCurrency(totals.closing)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* 3. Product-wise Sales Summary */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header bg-danger text-white">
                  <h5 className="card-title mb-0">
                    3. Product-wise Sales Summary
                  </h5>
                </div>
                <div className="card-body p-2">
                  {Object.entries(groupedProducts).map(
                    ([category, products], idx) => {
                      const sorted = [...products].sort((a, b) =>
                        a.productName.localeCompare(b.productName)
                      );

                      const totalQty = sorted.reduce(
                        (sum, p) => sum + p.netQuantity,
                        0
                      );

                      const totalSale = sorted.reduce(
                        (sum, p) => sum + p.netSale,
                        0
                      );

                      return (
                        <div className="mb-4" key={idx}>
                          <h6 className="fw-bold text-primary">{category}</h6>
                          <div className="table-responsive">
                            <table className="table table-bordered table-hover table-sm mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th className="text-center">Product</th>
                                  <th className="text-center">Net Qty</th>
                                  <th className="text-center">Net Sale</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sorted.map((item, i) => (
                                  <tr key={i}>
                                    <td>{item.productName}</td>
                                    <td className="text-end">
                                      {formatIndianCurrency(item.netQuantity)}
                                    </td>
                                    <td className="text-end fw-bold text-success">
                                      {formatIndianCurrency(item.netSale)}
                                    </td>
                                  </tr>
                                ))}

                                <tr className="table-secondary fw-bold">
                                  <td>Total</td>
                                  <td className="text-end">
                                    {formatIndianCurrency(totalQty)}
                                  </td>
                                  <td className="text-end">
                                    {formatIndianCurrency(totalSale)}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DistributorSummaryReport;